import { useState } from 'react';
import { Flex, ProgressCircle, TableView, TableHeader, TableBody, Column, Row, Cell, ActionButton } from '@adobe/react-spectrum';
import { useTranslation } from 'react-i18next';
import { useViewInfo } from '../../../hooks/UseViewInfo';
import styles from './InvoiceListTable.module.css';
import moment from 'moment';
import { CircleFill, InfoCircle } from 'react-bootstrap-icons';
import { isValidElement } from 'react';
interface IInvoiceListTableProps {
    invoiceList: any,
    handleTableRowclick: (tableRow: any) => void;
    isInvoiceMainView?: boolean
    handleEditClick?: (data: any) => void;
    handleDeleteClick?: (data: any) => void;
}

const InvoiceListTable: React.FC<IInvoiceListTableProps> = ({
    invoiceList,
    handleTableRowclick,
    handleEditClick,
    isInvoiceMainView }) => {
    const { t } = useTranslation('invoice');
    const _t = (key: string) => t(`invoice_table_column.${key}`, { ns: 'invoice' });
    let columnsData = [
        {
            key: 'status',
            name: _t('status'),
            width: 170,
        },
        {
            key: 'total',
            name: _t('total_excl_vat'),
            width: null,
        },
        {
            key: 'taxationDate',
            name: _t('taxation_date'),
            width: null,
        },
        {
            key: 'issuedDate',
            name: _t('issued_date'),
            width: null,
        },
        {
            key: 'customerName',
            name: _t('client'),
            width: null,
        }, {
            key: 'projectName',
            name: _t('project'),
            width: null,
        },
        {
            key: 'activityName',
            name: _t('activity'),
            width: null,
        }, {
            key: 'projectRefId',
            name: _t('project_ref_id'),
            width: null,
        }, {
            key: 'dueDate',
            name: _t('due_date'),
            width: null,
        },
        {
            key: 'info',
            name: '',
            width: 30,
        }
    ];

    let columnsDataMobile = [
        {
            key: 'icon',
            name: '',
            width: 25,
        },
        {
            key: 'dateTo',
            name: t('date', { ns: 'layout_components' }),
            width: 170,
        },
        {
            key: 'solutionStatus',
            name: t('status', { ns: 'layout_components' }),
            width: 150,
        },
        {
            key: 'title',
            name: t('subject', { ns: 'layout_components' }),
            width: 250,
        },
        {
            key: 'userList',
            name: t('user', { ns: 'layout_components' }),
            width: 250,
        },
        {
            key: 'entryTypeName',
            name: t('type_of_work', { ns: 'layout_components' }),
            width: 150,
        },
        { name: '', key: 'edit', width: 15 },
    ];

    const { isMobile } = useViewInfo();
    const [showLoader, setShowLoader] = useState(false);

    if (showLoader) {
        return (
            <Flex width="100%" justifyContent={'center'} marginTop={10}>
                <ProgressCircle aria-label={t('loading', { ns: 'layout_components', defaultValue: 'Loading...' })} isIndeterminate />
            </Flex>
        );
    } else {
        return (
            <Flex
                UNSAFE_style={{ padding: '0px' }}
                UNSAFE_className={styles.tables_scroll_container}
                direction={'column'}
                gap={'size-150'}
                position={'relative'}
                width={'100%'} >
                <Flex UNSAFE_style={{ paddingBottom: '10px' }} direction={'column'}>
                    <TableView
                        UNSAFE_className="tblLayoutComponent"
                        aria-label={t('invoice_list', { ns: 'invoice', defaultValue: 'Invoice list' })}
                        selectionMode="single"
                        selectionStyle="highlight"
                        width={{ base: '100%', L: '100%', M: '100%' }}
                        minHeight={'80px'}
                        maxHeight={{ base: '1000px', L: '650px' }}
                        onAction={handleTableRowclick}
                    >
                        <TableHeader columns={isMobile ? columnsDataMobile : columnsData}>
                            {column => (
                                <Column align={column.key === 'total' ? 'end' : undefined} showDivider key={column.key} width={column?.width}>
                                    {column.name}
                                </Column>
                            )}
                        </TableHeader>
                        <TableBody
                            items={invoiceList}>
                            {(item: any) =>
                                <Row>
                                    {(columnKey: any) => {
                                        if (columnKey === 'total') {
                                            return <Cell>{item[columnKey] && item[columnKey] > 0
                                                ? `${item[columnKey]} ${item['currency'] ? item['currency'] : ''}`
                                                : '0.00'}</Cell>
                                        }
                                        if (columnKey === 'taxationDate') {
                                            return <Cell>{moment(item[columnKey]).format('MM/DD/YYYY')}</Cell>
                                        }
                                        if (columnKey === 'issuedDate') {
                                            return <Cell>{moment(item[columnKey]).format('MM/DD/YYYY')}</Cell>
                                        }
                                        if (columnKey === 'dueDate') {
                                            return <Cell>{moment(item[columnKey]).format('MM/DD/YYYY')}</Cell>
                                        }
                                        if (columnKey === 'status') {
                                            if (isValidElement(item[columnKey])) {
                                                return <Cell>{item[columnKey]}</Cell>;
                                            }

                                            return (
                                                <Cell>
                                                    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                                        {item?.statusColor && (
                                                            <CircleFill
                                                                style={{ paddingRight: '10px' }}
                                                                size={'18px'}
                                                                color={item.statusColor}
                                                            />
                                                        )}
                                                        {item[columnKey]}
                                                    </span>
                                                </Cell>
                                            );
                                        }
                                        if(columnKey === 'info') {
                                            return <Cell><ActionButton isQuiet onPress={() => isInvoiceMainView ? handleTableRowclick(item.id): handleEditClick && handleEditClick(item)}><InfoCircle /></ActionButton></Cell>
                                        }

                                        return <Cell>{item[columnKey]}</Cell>
                                    }}
                                </Row>
                            }
                        </TableBody>
                    </TableView>

                </Flex>
            </Flex>
        );
    }
}

export default InvoiceListTable;
